import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Tag, Button, Spin, Typography, Space, Alert, Switch, message } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, DatabaseOutlined, PhoneOutlined, VideoCameraOutlined, ExperimentOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import moment from 'moment'
import Video from 'twilio-video'
import httpServiceEver from '../../services/httpServiceEver'
import httpServiceHealthCare from '../../services/httpServiceHealthCare'
import Layout from '../Home'

const isLocal = process.env.NODE_ENV === 'development'

const { Title, Text } = Typography

const Container = styled.div`
  padding: 16px;
  background: #f5f5f5;
  min-height: 100vh;
  width: 100%;
`

const HeaderCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  
  .ant-card-body {
    padding: 20px;
  }
`

const StatusCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  
  .ant-card-body {
    padding: 20px;
  }
`

const StatusTag = styled(Tag)`
  border-radius: 6px;
  font-weight: 500;
  padding: 4px 12px;
  font-size: 14px;
`

const OverallStatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  
  &.healthy {
    background: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
  }
  
  &.unhealthy {
    background: #fff2f0;
    color: #ff4d4f;
    border: 1px solid #ffccc7;
  }
`

const RefreshButton = styled(Button)`
  border-radius: 6px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
`

const Monitor = (props) => {
    const { history } = props
    const [healthData, setHealthData] = useState(null)
    const [telemedHealthData, setTelemedHealthData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [telemedError, setTelemedError] = useState(null)
    const [lastUpdated, setLastUpdated] = useState(null)
    const [recordingEnabled, setRecordingEnabled] = useState(false)
    const [recordingLoading, setRecordingLoading] = useState(false)
    const [recordingSaving, setRecordingSaving] = useState(false)
    const [testLoading, setTestLoading] = useState(false)
    const [testResult, setTestResult] = useState(null)

    const fetchHealthData = async () => {
        try {
            setLoading(true)
            setError(null)
            setTelemedError(null)

            let omaPromise, telemedPromise


            // Use service files for production
            omaPromise = httpServiceHealthCare.get('/HealthMonitors/check')
            telemedPromise = httpServiceEver.get('/HealthMonitors/check')


            const [omaResponse, telemedResponse] = await Promise.allSettled([
                omaPromise,
                telemedPromise
            ])

            if (omaResponse.status === 'fulfilled') {
                setHealthData(omaResponse.value.data)
            } else {
                setError(omaResponse.reason?.response?.data?.error?.message || omaResponse.reason?.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล OMA')
            }

            if (telemedResponse.status === 'fulfilled') {
                setTelemedHealthData(telemedResponse.value.data)
            } else {
                setTelemedError(telemedResponse.reason?.response?.data?.error?.message || telemedResponse.reason?.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล Telemed')
            }

            setLastUpdated(new Date())
        } catch (err) {
            setError(err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล')
            setHealthData(null)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchHealthData()
    }, [])

    const fetchRecordingStatus = async () => {
        setRecordingLoading(true)
        try {
            const res = await httpServiceHealthCare.get('/Communications/recording-status')
            if (res.data && typeof res.data.recording === 'boolean') {
                setRecordingEnabled(res.data.recording)
            }
        } catch (err) {
            message.error('โหลดสถานะการบันทึกวิดีโอไม่สำเร็จ')
        } finally {
            setRecordingLoading(false)
        }
    }

    useEffect(() => {
        fetchRecordingStatus()
    }, [])

    const handleRecordingToggle = async (checked) => {
        setRecordingSaving(true)
        try {
            await httpServiceHealthCare.put('/Communications/recording-status', {
                recording: checked,
            })
            setRecordingEnabled(checked)
            setTestResult(null)
            message.success(checked ? 'เปิดการบันทึกวิดีโอคอลแล้ว' : 'ปิดการบันทึกวิดีโอคอลแล้ว')
        } catch (err) {
            message.error(err?.response?.data?.error?.message || 'บันทึกการตั้งค่าไม่สำเร็จ')
        } finally {
            setRecordingSaving(false)
        }
    }

    const runRecordingTest = async () => {
        setTestLoading(true)
        setTestResult(null)
        let room = null
        try {
            const recordOnConnect = recordingEnabled
            const roomName = `monitor-test-${Date.now()}`
            const tokenRes = await httpServiceHealthCare.get('/Communications/getToken')
            const token = tokenRes.data?.token
            if (!token) {
                throw new Error('ไม่ได้รับ token')
            }
            room = await Video.connect(token, {
                name: roomName,
                RecordParticipantsOnConnect: recordOnConnect,
                video: false,
                audio: false,
            })
            const roomSid = room.sid
            await new Promise((r) => setTimeout(r, 5000))
            const statusRes = await httpServiceHealthCare.get('/Communications/room-recording-status', {
                params: { roomSid },
            })
            const { hasRecordings, count } = statusRes.data || {}
            setTestResult({
                success: true,
                recordOnConnect,
                roomSid,
                hasRecordings: !!hasRecordings,
                count: count || 0,
            })
        } catch (err) {
            setTestResult({
                success: false,
                error: err?.response?.data?.error?.message || err?.message || 'เกิดข้อผิดพลาด',
            })
        } finally {
            if (room && room.state === 'connected') {
                room.localParticipant.tracks.forEach((t) => t.track.stop())
                room.disconnect()
            }
            setTestLoading(false)
        }
    }

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-'
        const date = new Date(timestamp)
        return date.toLocaleString('th-TH', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        })
    }

    const isOverallHealthy = () => {
        // Check OMA database
        const omaDbHealthy = healthData?.checks?.database?.postgresql?.status === 'ok' &&
            healthData?.checks?.database?.mongodb?.status === 'ok'

        // Check Telemed database
        const telemedDbHealthy = telemedHealthData?.checks?.postgres?.status === 'ok' &&
            telemedHealthData?.checks?.mongo?.status === 'ok'

        // Check Twilio
        const twilioHealthy =
            healthData?.checks?.twilio?.token?.status === 'ok' &&
            healthData?.checks?.twilio?.account?.status === 'ok'

        return omaDbHealthy && telemedDbHealthy && twilioHealthy
    }

    const renderStatusTag = (status) => {
        if (status === 'ok') {
            return (
                <StatusTag color="success" icon={<CheckCircleOutlined />}>
                    OK
                </StatusTag>
            )
        }
        return (
            <StatusTag color="error" icon={<CloseCircleOutlined />}>
                Error
            </StatusTag>
        )
    }

    const renderDatabaseCard = (name, data) => {
        if (!data) return null

        return (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} key={name}>
                <StatusCard>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <DatabaseOutlined />
                                {name}
                            </Title>
                            {renderStatusTag(data.status)}
                        </div>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                            {data.message || '-'}
                        </Text>
                    </Space>
                </StatusCard>
            </Col>
        )
    }

    const renderTwilioCard = (name, data) => {
        if (!data) return null

        return (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} key={name}>
                <StatusCard>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <PhoneOutlined />
                                {name}
                            </Title>
                            {renderStatusTag(data.status)}
                        </div>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                            {data.message || '-'}
                        </Text>
                        {data.balance && (
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                Balance: {data.balance}
                            </Text>
                        )}
                        {data.accountStatus && (
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                Status: {data.accountStatus}
                            </Text>
                        )}
                    </Space>
                </StatusCard>
            </Col>
        )
    }

    return (
        <Layout
            history={history}
            render={() => (
                <Container>
                    <HeaderCard>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Title level={2} style={{ margin: 0 }}>
                                    Monitor System
                                </Title>
                            </Col>
                            <Col>
                                <Space>
                                    {lastUpdated && (
                                        <Text type="secondary" style={{ fontSize: '14px' }}>
                                            อัปเดตล่าสุด: {formatTimestamp(lastUpdated)}
                                        </Text>
                                    )}
                                    <RefreshButton
                                        type="primary"
                                        icon={<ReloadOutlined />}
                                        onClick={fetchHealthData}
                                        loading={loading}
                                    >
                                        รีเฟรช
                                    </RefreshButton>
                                </Space>
                            </Col>
                        </Row>
                    </HeaderCard>

                    {(error || telemedError) && (
                        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                            {error && (
                                <Alert
                                    message="เกิดข้อผิดพลาด OMA"
                                    description={error}
                                    type="error"
                                    showIcon
                                />
                            )}
                            {telemedError && (
                                <Alert
                                    message="เกิดข้อผิดพลาด Telemed"
                                    description={telemedError}
                                    type="error"
                                    showIcon
                                />
                            )}
                        </Space>
                    )}

                    {loading && !healthData && !telemedHealthData ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin size="large" />
                        </div>
                    ) : (healthData || telemedHealthData) ? (
                        <>
                            <HeaderCard>
                                <Row align="middle" gutter={16}>
                                    <Col>
                                        <Text strong style={{ fontSize: '16px', marginRight: '12px' }}>
                                            สถานะโดยรวม:
                                        </Text>
                                    </Col>
                                    <Col>
                                        <OverallStatusBadge className={isOverallHealthy() ? 'healthy' : 'unhealthy'}>
                                            {isOverallHealthy() ? (
                                                <>
                                                    <CheckCircleOutlined />
                                                    Healthy
                                                </>
                                            ) : (
                                                <>
                                                    <CloseCircleOutlined />
                                                    Unhealthy
                                                </>
                                            )}
                                        </OverallStatusBadge>
                                    </Col>
                                    {(healthData?.timestamp || telemedHealthData?.timestamp) && (
                                        <Col style={{ marginLeft: 'auto' }}>
                                            <Text type="secondary" style={{ fontSize: '14px' }}>
                                                OMA Timestamp: {healthData?.timestamp ? formatTimestamp(healthData.timestamp) : '-'} |
                                                Telemed Timestamp: {telemedHealthData?.timestamp ? formatTimestamp(telemedHealthData.timestamp) : '-'}
                                            </Text>
                                        </Col>
                                    )}
                                </Row>
                            </HeaderCard>

                            {healthData && (
                                <Card
                                    title={
                                        <span>
                                            <DatabaseOutlined style={{ marginRight: '8px' }} />
                                            OMA database
                                        </span>
                                    }
                                    style={{ marginBottom: 16, borderRadius: '12px' }}
                                >
                                    <Row gutter={[16, 16]}>
                                        {healthData.checks?.database?.postgresql &&
                                            renderDatabaseCard('PostgreSQL', healthData.checks.database.postgresql)}
                                        {healthData.checks?.database?.mongodb &&
                                            renderDatabaseCard('MongoDB', healthData.checks.database.mongodb)}
                                    </Row>
                                </Card>
                            )}

                            {telemedHealthData && (
                                <Card
                                    title={
                                        <span>
                                            <DatabaseOutlined style={{ marginRight: '8px' }} />
                                            Telemed Database
                                        </span>
                                    }
                                    style={{ marginBottom: 16, borderRadius: '12px' }}
                                >
                                    <Row gutter={[16, 16]}>
                                        {telemedHealthData.checks?.postgres &&
                                            renderDatabaseCard('PostgreSQL', telemedHealthData.checks.postgres)}
                                        {telemedHealthData.checks?.mongo &&
                                            renderDatabaseCard('MongoDB', telemedHealthData.checks.mongo)}
                                    </Row>
                                </Card>
                            )}

                            {healthData?.checks?.twilio && (
                                <Card
                                    title={
                                        <span>
                                            <PhoneOutlined style={{ marginRight: '8px' }} />
                                            Twilio
                                        </span>
                                    }
                                    style={{ marginBottom: 16, borderRadius: '12px' }}
                                >
                                    <Row gutter={[16, 16]}>
                                        {healthData.checks?.twilio?.token &&
                                            renderTwilioCard('Twilio Token', healthData.checks.twilio.token)}
                                        {healthData.checks?.twilio?.account &&
                                            renderTwilioCard('Twilio Account', healthData.checks.twilio.account)}
                                    </Row>
                                </Card>
                            )}

                        </>
                    ) : null}

                    <Card
                        title={
                            <span>
                                <VideoCameraOutlined style={{ marginRight: '8px' }} />
                                การบันทึกวิดีโอคอล (RecordParticipantsOnConnect)
                            </span>
                        }
                        style={{ marginBottom: 16, borderRadius: '12px' }}
                    >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Text type="secondary">
                                เปิด/ปิดการบันทึก — ค่าจะคงอยู่จนกว่าจะเปลี่ยน และใช้กับวิดีโอคอลที่เริ่มใหม่
                            </Text>
                            {recordingLoading ? (
                                <Spin />
                            ) : (
                                <Row align="middle" gutter={16}>
                                    <Col>
                                        <Switch
                                            checked={recordingEnabled}
                                            onChange={handleRecordingToggle}
                                            loading={recordingSaving}
                                            checkedChildren="เปิด"
                                            unCheckedChildren="ปิด"
                                        />
                                    </Col>
                                    <Col>
                                        <Text strong={true}>
                                            {recordingEnabled ? 'เปิดการบันทึก' : 'ปิดการบันทึก'}
                                        </Text>
                                    </Col>
                                </Row>
                            )}
                        </Space>
                    </Card>

                    <Card
                        title={
                            <span>
                                <ExperimentOutlined style={{ marginRight: '8px' }} />
                                ทดสอบการเชื่อมต่อและค่าการบันทึก
                            </span>
                        }
                        style={{ marginBottom: 16, borderRadius: '12px' }}
                    >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Text type="secondary">
                                สร้างห้องทดสอบด้วยค่าการบันทึกปัจจุบัน แล้วตรวจจาก Twilio ว่ามี recording จริงหรือไม่ (รอ ~5 วินาทีหลังเข้าห้อง)
                            </Text>
                            <Button
                                type="primary"
                                icon={<ExperimentOutlined />}
                                onClick={runRecordingTest}
                                loading={testLoading}
                                disabled={recordingLoading}
                            >
                                รันทดสอบ
                            </Button>
                            {testResult && (
                                <Card size="small" style={{ background: testResult.success ? '#f6ffed' : '#fff2f0', borderColor: testResult.success ? '#b7eb8f' : '#ffccc7' }}>
                                    {testResult.success ? (
                                        <Space direction="vertical" size="small">
                                            <Text strong>
                                                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                                เชื่อมต่อห้องทดสอบสำเร็จ
                                            </Text>
                                            <Text>ค่า RecordParticipantsOnConnect ที่ใช้: <Tag color={testResult.recordOnConnect ? 'green' : 'default'}>{testResult.recordOnConnect ? 'เปิด' : 'ปิด'}</Tag></Text>
                                            <Text>จาก Twilio ห้องนี้มี recording: <Tag color={testResult.hasRecordings ? 'green' : 'default'}>{testResult.hasRecordings ? `ใช่ (${testResult.count} รายการ)` : 'ไม่'}</Tag></Text>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                สถานะตรงกับที่ตั้งค่า: {testResult.recordOnConnect === testResult.hasRecordings ? 'ใช่' : 'ไม่ตรง — ตรวจสอบการตั้งค่าหรือรอสักครู่แล้วทดสอบใหม่'}
                                            </Text>
                                        </Space>
                                    ) : (
                                        <Text type="danger">
                                            <CloseCircleOutlined style={{ marginRight: 8 }} />
                                            {testResult.error}
                                        </Text>
                                    )}
                                </Card>
                            )}
                        </Space>
                    </Card>
                </Container>
            )}
        />
    )
}

export default Monitor

